import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldType } from 'types/FieldTypes';
import FormModal from 'components/modals/FormModal';
import { useFetchData } from 'services/api/useFetchData';

// Rename the name of the function:
const SubscriptionForm: React.FC<{ viewKey?: string }> = ({ viewKey }) => {
    const { t } = useTranslation();

    // Store the selected subscription type of the form
    const [subscriptionType, setSubscriptionType] = useState<'provisional' | 'legacy_paid'>('provisional');

    // Store the selected package key to determine which seat fields to show
    const [selectedPackage, setSelectedPackage] = useState<string | null>(null);

    // Store the selected status of the legacy paid type to show the cancellation date
    const [legacyStatus, setLegacyStatus] = useState<'active' | 'cancelled'>('active');

    // Fetch the packages to determine the seat options of the selected package
    const { response: fetchedPackages } = useFetchData({
        apiUrl: 'get_subscription_package_list',
        params: useMemo(() => ({ is_active: true, include_view_only: true }), [])
    });

    // Get the selected package object to determine which seat types it supports
    const selectedPackageObject = (fetchedPackages?.results || []).find((pkg: any) => pkg.key === selectedPackage);
    const hasFieldSeats = selectedPackageObject?.enable_field_seats ?? false;
    const hasLightSeats = selectedPackageObject?.enable_light_seats ?? false;

    // Determine the endpoint based on the subscription type
    const apiObject = subscriptionType === 'legacy_paid'
        ? 'legacy_paid_subscription_as_superuser'
        : 'provisional_subscription_as_superuser';

    // Set the environment and package section of both subscription types
    const environmentFields: FieldType[] = [
        {
            type: 'header',
            header: 'subscription.general.environment_header',
        },
        {
            name: 'environment',
            label: 'subscription.general.environment_label',
            type: 'dropdown',
            disabled_selected: 'forms.select_type_disabled_selected',
            showSearch: true,
            apiRequest: [{
                endpoint: 'get_environment_without_subscription_as_superuser_list',
                object: 'environment_without_subscription_as_superuser',
                params: { },
                useCache: true
            }],
            allowNoneOption: false,
        },
        {
            name: 'package',
            backendField: 'key',
            label: 'subscription.general.package_label',
            type: 'dropdown',
            disabled_selected: 'forms.select_type_disabled_selected',
            showSearch: false,
            allowNoneOption: false,
            apiRequest: [{
                endpoint: 'get_subscription_package_list',
                object: 'subscription_package',
                params: { is_active: true, include_view_only: true },
                useCache: true
            }],
            onChange: (e) => setSelectedPackage(e.target.value || null),
        },
        {
            type: 'multiselect',
            name: 'additional_modules',
            label: 'billing.general.additional_modules_label',
            apiObject: 'module_feature',
            disabled_selected: 'forms.select_type_disabled_selected',
            allowNoneOption: false,
            optionFormat: (option) => option.feature_translation,
            selectionFormat: 'feature_translation',
        },
    ];

    // Set the seats section, the seat types follow the selected package
    const seatsFields: FieldType[] = [
        {
            type: 'header',
            header: 'subscription.general.seats_header',
        },
        {
            name: 'seats',
            label: 'subscription.general.full_seats_label',
            type: 'number',
            placeholder: 'subscription.general.full_seats_placeholder',
            width: '50%',
            group: 'field-row',
        },
        // Only show the field seats when the selected package enables them
        ...(hasFieldSeats ? [{
            name: 'field_seats',
            label: 'subscription.general.field_seats_label',
            type: 'number',
            placeholder: 'subscription.general.field_seats_placeholder',
            width: '50%',
            group: 'field-row',
        } as FieldType] : []),
        // Only show the light seats for the legacy paid type when the selected package enables them
        ...(subscriptionType === 'legacy_paid' && hasLightSeats ? [{
            name: 'light_seats',
            label: 'subscription.general.light_seats_label',
            type: 'number',
            placeholder: 'subscription.general.field_seats_placeholder',
            width: '50%',
            group: 'field-row-2',
        } as FieldType] : []),
        // Only the legacy paid type registers the api access tier
        ...(subscriptionType === 'legacy_paid' ? [{
            name: 'api_tier',
            label: 'subscription.general.api_tier_label',
            type: 'dropdown',
            disabled_selected: 'forms.select_type_disabled_selected',
            showSearch: false,
            allowNoneOption: true,
            hardcodedOptions: [
                { value: 'standard', name: 'subscription.general.api_tier_standard'},
                { value: 'extended', name: 'subscription.general.api_tier_extended'},
            ],
            width: '50%',
            group: 'field-row-2',
        } as FieldType] : []),
    ];

    // Set the duration section, the provisional type starts on a date and the legacy
    // paid type ends on a date because its start date is calculated by the backend
    // from the prepaid billing period, a quarter continues monthly and three years yearly
    const durationFields: FieldType[] = [
        {
            type: 'header',
            header: 'subscription.general.duration_header',
        },
        ...(subscriptionType === 'legacy_paid' ? [{
            name: 'billing_period',
            label: 'subscription.general.billing_period_label',
            type: 'dropdown',
            disabled_selected: 'forms.select_type_disabled_selected',
            showSearch: false,
            allowNoneOption: false,
            hardcodedOptions: [
                { value: 'monthly', name: 'subscription.billing_period.monthly'},
                { value: 'quarterly', name: 'subscription.billing_period.quarterly'},
                { value: 'yearly', name: 'subscription.billing_period.yearly'},
                { value: 'three_yearly', name: 'subscription.billing_period.three_yearly'},
            ],
            width: '50%',
            group: 'field-row-next',
        } as FieldType] : [{
            name: 'duration',
            label: 'subscription.general.duration_label',
            type: 'dropdown',
            disabled_selected: 'forms.select_type_disabled_selected',
            showSearch: false,
            allowNoneOption: false,
            hardcodedOptions: [
                { value: 'monthly', name: 'subscription.duration.monthly_long'},
                { value: 'yearly', name: 'subscription.duration.yearly_long'},
            ],
            width: '50%',
            group: 'field-row-next',
        } as FieldType]),
        ...(subscriptionType === 'legacy_paid' ? [{
            type: 'date',
            name: 'end_date',
            label: 'subscription.general.end_date_label',
            helperText: 'subscription.general.end_date_helperText',
            width: '50%',
            group: 'field-row-next',
        } as FieldType, {
            type: 'date',
            name: 'customer_since',
            label: 'subscription.general.customer_since_label',
            helperText: 'subscription.general.customer_since_helperText',
        } as FieldType] : [{
            type: 'date',
            name: 'start_date',
            label: 'subscription.general.start_date_label',
            width: '50%',
            group: 'field-row-next',
        } as FieldType]),
    ];

    // Set the pricing section of the legacy paid type, the current price goes on the
    // subscription lines and the new price is applied automatically from its start date
    const pricingFields: FieldType[] = [
        {
            type: 'header',
            header: 'subscription.general.pricing_header',
            description: 'subscription.general.pricing_description',
        },
        {
            type: 'price',
            name: 'current_price',
            label: 'subscription.general.current_price_label',
            apiObject: 'currency',
            helperText: 'subscription.general.current_price_helperText',
        },
        {
            type: 'price',
            name: 'new_price',
            label: 'subscription.general.new_price_label',
            apiObject: 'currency',
            helperText: 'subscription.general.new_price_helperText',
            width: '50%',
            group: 'field-row-end',
        },
        {
            type: 'price',
            name: 'new_monthly_price',
            label: 'subscription.general.new_monthly_price_label',
            apiObject: 'currency',
            helperText: 'subscription.general.new_monthly_price_helperText',
            width: '50%',
            group: 'field-row-end',
        },
        {
            type: 'date',
            name: 'new_price_start_date',
            label: 'subscription.general.new_price_start_date_label',
            helperText: 'subscription.general.new_price_start_date_helperText',
        },
    ];

    // Set the status section of the legacy paid type, a cancelled status shows the
    // cancellation date and registers the subscription as cancelled right away
    const statusFields: FieldType[] = [
        {
            type: 'header',
            header: 'subscription.general.status_header',
        },
        {
            name: 'status',
            label: 'subscription.general.status_label',
            type: 'dropdown',
            disabled_selected: 'forms.select_type_disabled_selected',
            showSearch: false,
            allowNoneOption: false,
            hardcodedOptions: [
                { value: 'active', name: 'subscription.general.legacy_status_active' },
                { value: 'cancelled', name: 'subscription.general.legacy_status_cancelled' },
            ],
            onChange: (e) => setLegacyStatus(e.target.value === 'cancelled' ? 'cancelled' : 'active'),
        },
        // Only show the cancellation date when the status is cancelled
        ...(legacyStatus === 'cancelled' ? [{
            type: 'date',
            name: 'cancellation_date',
            label: 'subscription.general.cancellation_date_label',
            helperText: 'subscription.general.cancellation_date_helperText',
        } as FieldType] : []),
    ];

    // Combine the sections based on the subscription type
    const fields: FieldType[] = subscriptionType === 'legacy_paid'
        ? [...environmentFields, ...seatsFields, ...durationFields, ...pricingFields, ...statusFields]
        : [...environmentFields, ...seatsFields, ...durationFields];

    return (
        <>
            <div className='subscription-type-toggle'>
                <label>{t('subscription.general.subscription_type_label')}</label>
                <select
                    value={subscriptionType}
                    onChange={(e) => {
                        setSubscriptionType(e.target.value as 'provisional' | 'legacy_paid');

                        // Reset the tracked package and status because the form is remounted empty
                        setSelectedPackage(null);
                        setLegacyStatus('active');
                    }}>
                    <option value='provisional'>{t('subscription.general.subscription_type_provisional')}</option>
                    <option value='legacy_paid'>{t('subscription.general.subscription_type_legacy_paid')}</option>
                </select>
            </div>
            <FormModal
                key={subscriptionType}
                viewKey={viewKey}
                apiObject={apiObject}
                fields={fields}
            />
        </>
    );
};

export default SubscriptionForm;
